import React from 'react';
import PropTypes from 'prop-types';
import Language from '@material-ui/icons/Language';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableBody from '@material-ui/core/TableBody';
import Divider from '@material-ui/core/Divider';
import Hidden from '@material-ui/core/Hidden';

import ORAlert from 'components/ORAlert/ORAlert';
import RBACDeleteButton from 'components/RBACWrapper/RBACDeleteButton/RBACDeleteButton';
import { withStyles } from '@material-ui/core';
import { OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER } from 'constants/roles';
import SERVICES_WITH_NODE_SCALING from 'constants/servicesWithNodeScaling';

const propTypes = {
  classes: PropTypes.shape({}).isRequired,
  region: PropTypes.string.isRequired,
  hosts: PropTypes.arrayOf(
    PropTypes.shape({
      memoryMb: PropTypes.number,
    }),
  ).isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  changeNodeQuantity: PropTypes.func.isRequired,
  instanceId: PropTypes.string.isRequired,
  service: PropTypes.string.isRequired,
  statusDisplay: PropTypes.string.isRequired,
};

export function UnstyledNodeList({
  classes,
  region,
  hosts,
  userRoles,
  changeNodeQuantity,
  service,
  instanceId,
  statusDisplay,
}) {
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = React.useState(false);
  const [desiredQuantity, setDesiredQuantity] = React.useState(0);
  const [role, setRole] = React.useState(null);
  const nodeWord = desiredQuantity === 1 ? 'node' : 'nodes';

  const handleDeleteClick = (newQuantity, newRole) => {
    setDeleteConfirmationOpen(true);
    setDesiredQuantity(newQuantity);
    setRole(newRole);
  };
  const handleConfirmationOk = () => {
    changeNodeQuantity(service, instanceId, JSON.stringify({ desiredQuantity, role }));
    setDeleteConfirmationOpen(false);
  };
  const handleConfirmationCancel = () => {
    setDeleteConfirmationOpen(false);
  };

  const allowedUserRoles = [OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER];

  const renderTooltipText = (host) => {
    let text = '';
    if (!SERVICES_WITH_NODE_SCALING[service]) {
      text = 'Removing Nodes is not available for this service';
    } else if (
      host.quantity === SERVICES_WITH_NODE_SCALING[service][host.role].minQuantity &&
      statusDisplay === 'Ready'
    ) {
      text = 'You cannot go below the minimum node count';
    } else if (statusDisplay !== 'Ready') {
      text = 'Please wait for the cluster to be ready before removing additional nodes.';
    }
    return text;
  };

  const determineInvalidDeleteField = (host) => {
    if (
      !SERVICES_WITH_NODE_SCALING[service] ||
      host.quantity === SERVICES_WITH_NODE_SCALING[service][host.role].minQuantity ||
      statusDisplay !== 'Ready'
    ) {
      return true;
    }
    return false;
  };

  const nodeListItemsMobile = hosts.map((host) => {
    const usageText = `${host.diskSizeGb}GB Storage / ${host.memoryMb}MB Memory`;
    const tooltipText = renderTooltipText(host);
    const invalidDeleteField = determineInvalidDeleteField(host);

    return [...Array(host.quantity).keys()].map((num) => (
      <TableRow classes={{ root: classes.rowRoot }} key={num}>
        <TableCell classes={{ root: classes.nameText }} align="left">
          Node&nbsp;
          {num + 1}
          <br />
          <span className={classes.textMobile}>{usageText}</span>
          <br />
          <Language color="secondary" className={classes.languageIcon} />
          <span className={classes.textMobile}>{region}</span>
        </TableCell>
        <TableCell classes={{ root: classes.deleteIcon }} align="right">
          <RBACDeleteButton
            size="small"
            classes={{ listItemDeleteRoot: classes.makeSquared }}
            userAccess={userRoles}
            allowedAccess={allowedUserRoles}
            onClick={() => handleDeleteClick(host.quantity - 1, host.role)}
            invalidField={invalidDeleteField}
            tooltip={tooltipText}
          />
        </TableCell>
      </TableRow>
    ));
  });

  const nodeListItems = hosts.map((host) => {
    const usageText = `${host.diskSizeGb}GB Storage / ${host.memoryMb}MB Memory`;
    const tooltipText = renderTooltipText(host);
    const invalidDeleteField = determineInvalidDeleteField(host);

    return [...Array(host.quantity).keys()].map((num) => (
      <TableRow classes={{ root: classes.rowRoot }} key={num}>
        <TableCell classes={{ root: classes.nameText }}>
          Node&nbsp;
          {num + 1}
        </TableCell>
        <TableCell classes={{ root: classes.storageText }}>{usageText}</TableCell>
        <TableCell classes={{ root: classes.regionText }}>
          <Language color="secondary" className={classes.languageIcon} />
          {region}
        </TableCell>
        <TableCell classes={{ root: classes.deleteIcon }} align="right">
          <RBACDeleteButton
            classes={{ listItemDeleteRoot: classes.makeSquared }}
            size="small"
            userAccess={userRoles}
            allowedAccess={allowedUserRoles}
            onClick={() => handleDeleteClick(host.quantity - 1, host.role)}
            invalidField={invalidDeleteField}
            tooltip={tooltipText}
          />
        </TableCell>
      </TableRow>
    ));
  });

  return (
    <React.Fragment>
      <Divider classes={{ root: classes.divider }} />
      <Table>
        <TableBody>
          <Hidden smDown>{nodeListItems}</Hidden>
          <Hidden mdUp>{nodeListItemsMobile}</Hidden>
        </TableBody>
      </Table>
      <ORAlert
        open={deleteConfirmationOpen}
        message={`You’ve selected to scale down from ${
          desiredQuantity + 1
        } to ${desiredQuantity} ${nodeWord}. This action cannot be undone.`}
        onOk={handleConfirmationOk}
        onCancel={handleConfirmationCancel}
      />
    </React.Fragment>
  );
}

const styles = (theme) => ({
  secondaryListRoot: {
    right: 0,
  },
  divider: {
    width: '100%',
    borderTop: `1px solid ${theme.customColors.mediumGrey}`,
  },
  listGutters: {
    paddingLeft: '0',
  },
  listRoot: {
    padding: '0',
    margin: '0',
  },
  languageIcon: {
    verticalAlign: 'middle',
    marginRight: theme.spacing(0.5),
  },
  rowRoot: {
    borderColor: theme.customColors.mediumGrey,
  },
  nameText: {
    paddingLeft: 0,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.customColors.mediumGrey}`,
  },
  regionText: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    color: theme.customColors.mediumGrey,
    borderBottom: `1px solid ${theme.customColors.mediumGrey}`,
  },
  storageText: {
    paddingRight: 0,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    color: theme.customColors.mediumGrey,
    borderBottom: `1px solid ${theme.customColors.mediumGrey}`,
  },
  deleteIcon: {
    paddingRight: 0,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.customColors.mediumGrey}`,
  },
  textMobile: {
    color: theme.customColors.mediumGrey,
  },
  makeSquared: {
    borderRadius: '4px',
  },
});

UnstyledNodeList.propTypes = propTypes;

export default withStyles(styles)(UnstyledNodeList);
